import * as React from "react";
import { cn } from "@/lib/utils";
import ButtonV2 from "./ButtonV2";

export interface AlertV2Props {
  variant?: "info" | "success" | "warning" | "error" | "destructive";
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const VARIANT_CLASSES: Record<
  "info" | "success" | "warning" | "error",
  string
> = {
  info: "v2-alert-info",
  success: "v2-alert-success",
  warning: "v2-alert-warning",
  error: "v2-alert-danger",
};

const VARIANT_ICONS: Record<"info" | "success" | "warning" | "error", string> =
  {
    info: "ℹ",
    success: "✓",
    warning: "⚠",
    error: "✕",
  };

export function AlertV2({
  variant = "info",
  title,
  children,
  dismissible = false,
  onDismiss,
  className,
  action,
}: AlertV2Props) {
  const resolvedVariant = variant === "destructive" ? "error" : variant;
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;

  const isUrgent = resolvedVariant === "error" || resolvedVariant === "warning";

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      role={isUrgent ? "alert" : "status"}
      className={cn(
        "flex items-start gap-3 rounded-[var(--v2-radius-md)] border px-4 py-3 text-sm",
        VARIANT_CLASSES[resolvedVariant],
        className
      )}
    >
      <span
        className="mt-0.5 text-base leading-none shrink-0"
        aria-hidden="true"
      >
        {VARIANT_ICONS[resolvedVariant]}
      </span>

      <div className="flex-1 space-y-1">
        {title && <p className="font-semibold">{title}</p>}
        <div className="leading-relaxed">{children}</div>
        {action ? <div className="pt-2">{action}</div> : null}
      </div>

      {dismissible && (
        <ButtonV2
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          aria-label="Uyarıyı kapat"
          className="-mr-1 mt-0.5 h-5 min-h-0 w-5 shrink-0 rounded p-0 text-current opacity-60 hover:opacity-100"
        >
          ✕
        </ButtonV2>
      )}
    </div>
  );
}
